// FASE 05 — S4-B4 (plano docs/19-08-2026): importacao de planilha de precos.
// Espec. secao 7 do mockup 29. Reaproveita o endpoint ImportarPlanilha que ja existia
// no legado — inclusive o log linha a linha ("Componente não encontrado").
//
// Plano 14/10/2026, Frente 3: as duas abas da spec (§7.1-7.3) — "Última planilha importada"
// com a lixeira (desfaz a planilha enquanto a tabela não vai para Disponível Mercado) e
// "Histórico de importação" com o download das planilhas já concluídas.

import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { ILogImportacaoLinha, IPlanilhaImportada } from "../../types/tabelaPreco.types";
import { obterHistoricoImportacao } from "../../services/tabelaPrecoService";
import { baixarModeloPlanilha } from "../helpers/planilhaPrecos";
import { ModalConfirmacao } from "./ModalConfirmacao";
import { ListaCopiarBaixar } from "components/ListaCopiarBaixar";

// Textos da spec 29 (§7.2-7.3) e do plano 14/10/2026 (Frente 3).
const TOOLTIP_HISTORICO =
  "Quando a tabela de preços passar para a situação Disponível Mercado, o arquivo utilizado será " +
  "gravado nessa aba Histórico para que o usuário possa fazer o download das últimas planilhas " +
  "que subiu para o MOF para conferência futura.";
const MSG_EXCLUIR_PLANILHA =
  "As alterações desta planilha serão desfeitas e a tabela volta ao que estava antes da importação.";
const COR_PRIMARIA = "#3C90AD";

const FORMATO_DATA_HORA: Intl.DateTimeFormatOptions = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
};

const formatarDataHora = (valor: string): string => {
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return valor || "";
  return data.toLocaleString("pt-BR", FORMATO_DATA_HORA);
};

type AbaImportacao = "importar" | "historico";

interface ModalImportarProps {
  open: boolean;
  idTabelaPreco: number;
  tipoMaterial: number;
  // Plano 14/10/2026, Frente 3: lote de trabalho com arquivo (null = nenhuma pendente).
  planilhaPendente: IPlanilhaImportada | null;
  onClose: () => void;
  onImportar: (arquivo: File) => Promise<ILogImportacaoLinha[] | null>;
  // Devolve true quando a planilha foi excluída (a tela pai recarrega tabela e estado).
  onExcluirPlanilha: () => Promise<boolean>;
}

export const ModalImportar: React.FC<ModalImportarProps> = ({
  open,
  idTabelaPreco,
  tipoMaterial,
  planilhaPendente,
  onClose,
  onImportar,
  onExcluirPlanilha,
}) => {
  const [aba, setAba] = useState<AbaImportacao>("importar");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [importando, setImportando] = useState(false);
  const [logs, setLogs] = useState<ILogImportacaoLinha[]>([]);
  const [erro, setErro] = useState("");
  const [confirmarExclusaoAberto, setConfirmarExclusaoAberto] = useState(false);
  const [historico, setHistorico] = useState<IPlanilhaImportada[]>([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [erroHistorico, setErroHistorico] = useState("");

  useEffect(() => {
    if (!open) {
      setAba("importar");
      setArquivo(null);
      setLogs([]);
      setErro("");
      setConfirmarExclusaoAberto(false);
      setHistorico([]);
      setErroHistorico("");
    }
  }, [open]);

  const carregarHistorico = useCallback(async () => {
    setCarregandoHistorico(true);
    try {
      const { itens, erro: falha } = await obterHistoricoImportacao(idTabelaPreco);
      setHistorico(itens);
      setErroHistorico(falha ?? "");
    } finally {
      setCarregandoHistorico(false);
    }
  }, [idTabelaPreco]);

  useEffect(() => {
    if (open && aba === "historico") carregarHistorico();
  }, [open, aba, carregarHistorico]);

  // Plano 14/10/2026, Frente 2: com uma planilha pendente o servidor recusa outra — é preciso
  // concluir (disponibilizar) ou excluir a atual primeiro.
  const importacaoBloqueada = Boolean(planilhaPendente);

  const handleImportar = async () => {
    if (!arquivo) {
      setErro("Selecione a planilha que será importada.");
      return;
    }

    setImportando(true);
    setLogs([]);
    try {
      const resultado = await onImportar(arquivo);
      // Log vazio significa importacao aceita — quem fecha o modal e a tela pai.
      if (resultado && resultado.length > 0) setLogs(resultado);
    } finally {
      setImportando(false);
    }
  };

  const botaoAba = (valor: AbaImportacao, rotulo: string) => (
    <Button
      variant="contained"
      onClick={() => setAba(valor)}
      disabled={importando}
      sx={{
        backgroundColor: aba === valor ? COR_PRIMARIA : "#75B0C7",
        color: "#fff",
        borderRadius: 0,
        px: 4,
        py: 1.5,
        fontWeight: 500,
        fontSize: "0.85rem",
        boxShadow: "none",
        "&:hover": { backgroundColor: aba === valor ? "#2d6f87" : "#5a9ab5", boxShadow: "none" },
      }}
    >
      {rotulo}
    </Button>
  );

  const iconeAjudaHistorico = (
    <Tooltip title={TOOLTIP_HISTORICO}>
      <InfoOutlinedIcon sx={{ fontSize: 18, color: "#666", cursor: "help" }} />
    </Tooltip>
  );

  return (
    <Dialog open={open} onClose={importando ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography component="span" sx={{ fontWeight: 600 }}>
          Importação de tabela de preços
        </Typography>
        <IconButton onClick={onClose} size="small" disabled={importando}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", mb: 2 }}>
          {botaoAba("importar", "IMPORTAR TABELA DE PREÇOS")}
          {botaoAba("historico", "HISTÓRICO DE IMPORTAÇÃO")}
        </Box>

        {aba === "importar" && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              A planilha substitui os preços em edição desta tabela. Produtos que não estiverem
              nas referências liberadas para a empresa são recusados e aparecem no log abaixo.
            </Typography>

            {/* Plano 30/08/2026, Frente 9: layout do mockup enviado pelo TI em 30/08 —
                BAIXAR PLANILHA PADRÃO, campo com o botão BUSCAR e IMPORTAR desabilitado até
                haver arquivo, com a legenda ao lado explicando o motivo. */}
            <Button
              variant="contained"
              onClick={() => baixarModeloPlanilha(tipoMaterial)}
              disabled={importando}
              sx={{
                alignSelf: "flex-start",
                backgroundColor: COR_PRIMARIA,
                "&:hover": { backgroundColor: "#2d6f87" },
                borderRadius: 0,
                fontWeight: 600,
                px: 3,
              }}
            >
              BAIXAR PLANILHA PADRÃO
            </Button>

            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: "#555" }}>
                PLANILHA XLSX
              </Typography>
              {iconeAjudaHistorico}
            </Stack>

            <Stack direction="row" alignItems="stretch" sx={{ width: "100%" }}>
              <Button
                component="label"
                variant="contained"
                disabled={importando}
                sx={{
                  backgroundColor: "#6BA8BF",
                  "&:hover": { backgroundColor: "#5a97ae" },
                  borderRadius: 0,
                  fontWeight: 600,
                  px: 4,
                  whiteSpace: "nowrap",
                }}
              >
                BUSCAR
                <input
                  hidden
                  type="file"
                  accept=".xls,.xlsx"
                  onChange={(e) => {
                    setArquivo(e.target.files?.[0] ?? null);
                    setErro("");
                    setLogs([]);
                  }}
                />
              </Button>
              <Stack
                justifyContent="center"
                sx={{
                  flex: 1,
                  border: "1px solid #c4c4c4",
                  borderLeft: "none",
                  px: 2,
                  minHeight: 42,
                  overflow: "hidden",
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ color: arquivo ? "text.primary" : "text.disabled" }}
                >
                  {arquivo?.name ?? ""}
                </Typography>
              </Stack>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Button
                onClick={handleImportar}
                variant="contained"
                disabled={importando || !arquivo || importacaoBloqueada}
                startIcon={importando ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  backgroundColor: COR_PRIMARIA,
                  "&:hover": { backgroundColor: "#2d6f87" },
                  borderRadius: 0,
                  fontWeight: 600,
                  px: 4,
                }}
              >
                IMPORTAR
              </Button>
              {importacaoBloqueada ? (
                <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                  Há uma planilha importada em andamento. Disponibilize a tabela ou exclua a
                  planilha abaixo para importar outra.
                </Typography>
              ) : (
                !arquivo && (
                  <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                    Selecione uma planilha (.xlsx ou .xls) para habilitar a importação
                  </Typography>
                )
              )}
            </Stack>

            {erro && <Alert severity="warning">{erro}</Alert>}

            {logs.length > 0 && (
              <>
                <Alert severity="error">
                  A importação não foi concluída: {logs.length} linha(s) com problema.
                </Alert>
                {/* Plano 21/10/2026 (Frente 7): lista para copiar/baixar — inclui o tamanho de barra
                    que a planilha trouxe e não existe no cadastro do perfil. */}
                <ListaCopiarBaixar
                  nomeArquivo="importacao-precos-problemas"
                  colunas={[{ titulo: "Linha" }, { titulo: "Código" }, { titulo: "Tamanho de barra" }, { titulo: "Mensagem" }]}
                  linhas={logs.map((log) => [log.numeroLinhaArquivo, log.codigo, log.tamanhoBarra ?? "", log.mensagem])}
                />
              </>
            )}

            {/* Spec §7.2: "Última planilha importada" — no máximo 1 registro, com a lixeira. */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ pt: 1 }}>
              <Typography sx={{ color: "#666", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                Última planilha importada
              </Typography>
              <Divider sx={{ flex: 1 }} />
            </Stack>

            {planilhaPendente ? (
              <TableContainer sx={{ border: "1px solid #e0e0e0" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "55%" }}>Data/hora</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "35%" }}>Situação</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "10%" }} align="center">
                        Ação
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>{formatarDataHora(planilhaPendente.dataHora)}</TableCell>
                      <TableCell>{planilhaPendente.situacao}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="Excluir a planilha e desfazer as alterações dela">
                          <span>
                            <IconButton
                              size="small"
                              sx={{ color: "#d32f2f" }}
                              disabled={importando}
                              onClick={() => setConfirmarExclusaoAberto(true)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography sx={{ color: "#999", fontSize: "0.85rem", fontStyle: "italic" }}>
                Nenhuma planilha importada em andamento.
              </Typography>
            )}
          </Stack>
        )}

        {/* Spec §7.3: planilhas das versões concluídas, mais recente primeiro, com download. */}
        {aba === "historico" && (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              {iconeAjudaHistorico}
              <Typography variant="body2" color="text.secondary">
                Planilhas importadas cujo processo já foi concluído, da mais recente para a mais antiga.
              </Typography>
            </Stack>

            {erroHistorico && <Alert severity="error">{erroHistorico}</Alert>}

            {carregandoHistorico ? (
              <Stack alignItems="center" sx={{ py: 4 }}>
                <CircularProgress size={28} />
              </Stack>
            ) : historico.length === 0 ? (
              <Typography sx={{ color: "#999", fontSize: "0.85rem", textAlign: "center", py: 4 }}>
                Nenhum histórico de importação encontrado.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 360, border: "1px solid #e0e0e0" }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, width: "60%" }}>
                        Data/hora atualização
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "30%" }}>Situação</TableCell>
                      <TableCell sx={{ fontWeight: 600, width: "10%" }} align="center">
                        Ação
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {historico.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>{formatarDataHora(item.dataHora)}</TableCell>
                        <TableCell>{item.situacao}</TableCell>
                        <TableCell align="center">
                          <Tooltip title="Baixar a planilha importada">
                            <IconButton
                              size="small"
                              sx={{ color: COR_PRIMARIA }}
                              onClick={() => window.open(item.link, "_blank")}
                            >
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Stack>
        )}
      </DialogContent>
      {/* O IMPORTAR vive no corpo do modal, como no mockup — aqui fica só o fechar. */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined" disabled={importando} sx={{ color: "#666", borderColor: "#bdbdbd" }}>
          Fechar
        </Button>
      </DialogActions>

      <ModalConfirmacao
        open={confirmarExclusaoAberto}
        titulo="Excluir planilha importada?"
        mensagem={MSG_EXCLUIR_PLANILHA}
        textoConfirmar="Excluir"
        onClose={() => setConfirmarExclusaoAberto(false)}
        onConfirmar={async () => {
          await onExcluirPlanilha();
          setConfirmarExclusaoAberto(false);
        }}
      />
    </Dialog>
  );
};
